import Image from "next/image"
import Link from "@/components/Link"
import { AspectRatio } from "@/components/ui/aspect-ratio"
import { getImagePath } from "@/lib/helpers/imageHelpers"
import { User, Clock, Tag } from "lucide-react"
import { formatRelativeTime } from "@/lib/utils"
import type { BlogDetail } from "@/lib/types/blog/detail"
import { Badge } from "./Badge"

interface BlogCardProps {
	data: BlogDetail
	tags: boolean
}

export const BlogCard = ({ data: blogData, tags }: BlogCardProps) => (
	<Link className="block w-full cursor-pointer rounded-lg border border-gray-200" href={`/blog/${blogData.slug}`}>
		<div className="mx-auto overflow-hidden rounded-lg bg-white shadow-lg transition-all duration-300 ease-in-out hover:scale-105 md:max-w-sm">
			<div className="line-clamp-2 h-full min-h-[80px] p-2 font-semibold text-gray-800 md:p-4">
				{blogData.name}
			</div>
			<div className="h-[48px] px-2">
				<p className="line-clamp-2 text-gray-500 text-sm">
					{blogData.description}
				</p>
			</div>
			<div className="relative w-full">
				<AspectRatio ratio={16 / 9} className="bg-muted">
					<Image
						src={getImagePath(blogData, "blog")}
						alt={blogData.name}
						fill
						sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
						className="object-cover"
						priority={true}
					/>
					<div className="absolute right-0 bottom-0 rounded-tl-lg bg-[#2e7d32] px-2 py-1 text-sm text-white">
						{blogData?.category_name}
					</div>
				</AspectRatio>
			</div>
			<div className="p-4 transition-colors duration-300 ease-in-out hover:bg-gray-50">
				<div className="mt-2 flex items-center justify-between text-gray-500 text-xs">
					<span className="flex items-center gap-1">
						<User className="h-3 w-3" />
						{blogData.user_fullname}
					</span>
					<span className="flex items-center gap-1">
						<Clock className="h-3 w-3" />
						{formatRelativeTime(blogData.created_at)}
					</span>
				</div>
				{tags && (
					<div className="mt-2 flex flex-wrap gap-2">
						{Array.isArray(blogData.tag) && blogData.tag.length > 0 ? (
							blogData.tag.map((tag: string) => (
								<Badge
									key={tag}
									icon={<Tag className="h-3 w-3" />}
									text={tag}
								/>
							))
						) : (
							<span className="flex items-center gap-1 text-gray-500 text-sm">
								<Tag className="h-3 w-3" />
								No tags available
							</span>
						)}
					</div>
				)}
			</div>
		</div>
	</Link>
)
